import { Modal, Button, Form, Input, Select } from "antd";
import type { ManualActionInput } from "@/utils/userActionsStore";

const { TextArea } = Input;

const PRIORITY_OPTIONS = [
  { value: "Critical", label: "Critical" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

type AddActionFormValues = {
  title?: string;
  priority?: string;
  assignee?: string;
  dueDate?: string;
  linkedRisk?: string;
  description?: string;
};

type AddActionModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Creates the action. The Actions screen passes this so the form really adds
   * a row; Quality > Tasks omits it and the form stays a mock over the seeded
   * RBQM records, which are not user-editable.
   */
  onCreate?: (input: ManualActionInput) => void;
};

export default function AddActionModal({
  open,
  onClose,
  onCreate,
}: AddActionModalProps) {
  const [form] = Form.useForm<AddActionFormValues>();

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async (status: string) => {
    if (!onCreate) {
      handleClose();
      return;
    }

    let values: AddActionFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // Ant Design has marked the offending fields; leave the form open.
      return;
    }

    const title = values.title?.trim();
    if (!title) return;

    onCreate({
      title,
      description: values.description,
      priority: values.priority,
      assignedTo: values.assignee,
      dueDate: values.dueDate,
      linkedRisk: values.linkedRisk,
      status,
    });
    handleClose();
  };

  return (
    <Modal
      title="Add task"
      open={open}
      onCancel={handleClose}
      width={520}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={() => handleSubmit("Draft")}>Save as draft</Button>
          <Button type="primary" onClick={() => handleSubmit("Open")}>
            Create task
          </Button>
        </div>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" className="mt-2">
        <Form.Item
          label="Task title"
          name="title"
          rules={
            onCreate
              ? [{ required: true, message: "Give the task a title" }]
              : undefined
          }
        >
          <Input placeholder="e.g. Escalate Site 03 AE reporting to medical monitor" />
        </Form.Item>
        <div className="flex gap-3">
          <Form.Item label="Task type" name="type" className="flex-1">
            <Select
              placeholder="Select type"
              options={[
                { value: "escalation", label: "Escalation" },
                { value: "review", label: "Review" },
                { value: "follow-up", label: "Site follow-up" },
                { value: "sign-off", label: "Sign-off" },
                { value: "capa", label: "CAPA action" },
                { value: "other", label: "Other" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Priority" name="priority" className="flex-1">
            <Select placeholder="Select priority" options={PRIORITY_OPTIONS} />
          </Form.Item>
        </div>
        <div className="flex gap-3">
          <Form.Item label="Assign to" name="assignee" className="flex-1">
            <Input placeholder="Search team members or roles..." />
          </Form.Item>
          <Form.Item label="Due date" name="dueDate" className="flex-1">
            <Input placeholder="e.g. May 22, 2026" />
          </Form.Item>
        </div>
        <Form.Item label="Linked risk / issue (optional)" name="linkedRisk">
          <Input placeholder="Search risks or issues..." />
        </Form.Item>
        <Form.Item label="Description (optional)" name="description">
          <TextArea
            placeholder="Additional context or instructions..."
            rows={3}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
